import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { CircularProgress, CssBaseline } from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import mixpanel from "mixpanel-browser";
import { type FC, lazy, type ReactNode, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Navigate, Outlet, type RouteObject, useLocation, useNavigate, useParams, useRoutes } from "react-router-dom";
import { ChatBot } from "./components/Chat/ChatBot";
import { ChartContainer } from "./components/chartContainer";
import { AdminUserGuides } from "./components/docs/admin-user-guides/AdminUserGuides";
import { StaffUserGuides } from "./components/docs/staff-user-guides/StaffUserGuides";
import { ForceUpdateScreen } from "./components/ForceUpdate/ForceUpdateScreen";
import { PlanningOverview } from "./components/planning";
import { RequestUtilsProvider } from "./components/RequestData/Provider/RequestUtilsProvider";
import { RequestRouteWrapper } from "./components/RequestData/RequestRouteWrapper";
import { ShiftData } from "./components/ShiftData/ShiftData";
import { StaffCommunication } from "./components/StaffCommunication/StaffCommunication";
import { MonthlyViewRoute } from "./components/staffing/MonthlyView/jobView/MonthlyViewRoute";
import { AddNurseToShift } from "./components/staffing/mobile/AddNurseToShift/AddNurseToShift";
import { BroadcastMessage } from "./components/staffing/mobile/BroadcaseMessage/BroadcastMessage";
import { MobileStaffing } from "./components/staffing/mobile/MobileStaffing";
import { PostShift } from "./components/staffing/mobile/PostShift/PostShift";
import { MobileStaffingRouteSwitch } from "./components/staffing/mobileGaps/MobileStaffingRouteSwitch";
import { RequestsRouteSwitch } from "./components/staffing/mobileGaps/RequestsRouteSwitch";
import { StaffingWrapper } from "./components/staffing/StaffingWrapper";
import { TermsAndConditionsDialog } from "./components/TermsAndConditionsDialog";
import { Team } from "./components/team/Team";
import { type UserInfo, useAuth } from "./contexts/AuthProvider";
import { AnalyticValuesProvider } from "./contexts/analytics/AnalyticValuesProvider";
import { ChoseDepartmentProvider } from "./contexts/ChosenDepartment";
import { CloseOpenShiftSuggestionProvider } from "./contexts/CloseOpenShiftSuggestionProvider";
import { DraftModeProvider } from "./contexts/DraftModeProvider";
import { FEATURE_FLAGS, FeatureFlagsProvider } from "./contexts/FeatureFlagsProvider";
import { HospitalDataProvider } from "./contexts/HospitalDataProvider";
import { I18nProvider } from "./contexts/i18nProvider";
import { ModalProvider } from "./contexts/ModalProvider";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { PopupManagerProvider } from "./contexts/PopupManagerContext";
import { RecommendationsProvider } from "./contexts/Recommendations/RecommendationsProvider";
import { RecommendationsDrawerProvider } from "./contexts/RecommendationsDrawerContext";
import { RequestsProvider } from "./contexts/RequestsContext";
import { SelfSchedulingProvider } from "./contexts/SelfScheduling/SelfSchedulingProvider";
import { GlobalManualLocationOrderProvider } from "./contexts/Staffing/GlobalManualLocationOrderProvider";
import { UserSettingsProvider } from "./contexts/Staffing/UserSettingsProvider";
import { TooltipProvider } from "./contexts/TooltipContext";
import { UserGuideChatProvider } from "./contexts/UserGuideChatProvider";
import { WeekIndexProvider } from "./contexts/WeekIndexProvider";
import { WeekViewProvider } from "./contexts/WeekView";
import { useIsMobile } from "./hooks/useMobile";
import { useOnReactClientInactive } from "./hooks/useOnReactClientInactive";
import { useFeatureFlag } from "./hooks/useOpenFeatureFlag";
import { usePushSettingsTracker } from "./hooks/usePushSettingsTracker";
import { RevealWageProvider } from "./hooks/useRevealWage";
import { useUniversalLink } from "./hooks/useUniversalLink";
import { ActivityLogPage } from "./pages/ActivityLog/ActivityLogPage";
import { BedsideHome } from "./pages/BedsideHome/BedsideHome";
import { CallIns } from "./pages/CallIns/CallIns";
import { CommandCenter } from "./pages/CommandCenter";
import { DaySchedule } from "./pages/DaySchedule/DaySchedule";
import { DayScheduleMobile } from "./pages/DaySchedule/Mobile/DayScheduleMobile";
import DisciplinaryReport from "./pages/DisciplinaryReport/DisciplinaryReport";
import { EmployeeChangelog } from "./pages/EmployeeChangelog/EmployeeChangelog";
import { FacilityPage } from "./pages/FacilityPage/FacilityPage";
import HiringModuleWrapper from "./pages/HiringModule/HiringModuleWrapper";
import LeakageReport from "./pages/LeakageReport/LeakageReport";
import { Layout } from "./pages/layout";
import { CostDeepDiveReport } from "./pages/ManagementCenter/CostDeepDive/CostDeepDiveReport";
import { HoursSummaryReport } from "./pages/ManagementCenter/HoursSummary/HoursSummaryReport";
import { ManagementCenter } from "./pages/ManagementCenter/ManagementCenter";
import { ScheduleVsActualReport } from "./pages/ManagementCenter/ScheduleVsActual/ScheduleVsActualReport";
import { StaffSatisfactionReport } from "./pages/ManagementCenter/StaffSatisfaction/StaffSatisfactionReport";
import { ManagementCenterMulti } from "./pages/ManagementCenterNetwork/ManagementCenterMulti";
import { ScheduleTemplatePageWrapper } from "./pages/MasterSchedule/ScheduleTemplatePageWrapper";
import { NotificationsPage } from "./pages/NotificationsPage/NotificationsPage";
import { NurseSchedule } from "./pages/NurseSchedule";
import OnboardingToolsPage from "./pages/OnboardingTools/OnboardingToolsPage";
import { OpenShifts } from "./pages/OpenShifts/OpenShifts";
import { Patients } from "./pages/Patients/Patients";
import { PbjCalculator } from "./pages/PbjCalculator/PbjCalculator";
import { PbjModelPage } from "./pages/PbjModel/PbjModelPage";
import { Preferences } from "./pages/Preferences/Preferences";
import { ProfileV2 } from "./pages/Profile/v2/ProfileV2";
import PageNotFound from "./pages/pageNotFound/PageNotFound";
import { Schedule } from "./pages/Schedule/Schedule";
import { SelfSchedulingWrapper } from "./pages/SelfScheduling/SelfSchedulingWrapper";
import { DepartmentConfiguration } from "./pages/Settings/DepartmentConfiguration/DepartmentConfiguration";
import { AdminNotificationSettings } from "./pages/Settings/Notifications/AdminNotificationSettings";
import { NotificationSettings } from "./pages/Settings/Notifications/NotificationSetting";
import { NotificationTypeSettings } from "./pages/Settings/Notifications/NotificationTypeSettings";
import { Settings } from "./pages/Settings/Settings";
import SftpSetupPage from "./pages/SftpSetup/SftpSetupPage";
import ShiftPickupReport from "./pages/ShiftPickupReport/ShiftPickupReport";
import { CreateAccount } from "./pages/signin/CreateAccount";
import { EmailVerification } from "./pages/signin/EmailVerification";
import { ForgotPassword } from "./pages/signin/ForgotPassword";
import { MultiFactorAuth } from "./pages/signin/MFA";
import { OneTimePassword } from "./pages/signin/OTP";
import { ResetPasswordOrWelcome } from "./pages/signin/ResetPasswordOrWelcome";
import { SignIn } from "./pages/signin/SignIn";
import { Welcome } from "./pages/signin/Welcome";
import { localStorageService } from "./services/LocalStorageService";
import { pushNotificationService } from "./services/pushNotification.service";
import { universalLinkService } from "./services/universalLink.service";
import { DepartmentLookSync } from "./theme/DepartmentLookSync";
import { HospitalDemoThemeProvider } from "./theme/HospitalDemoThemeProvider";
import { ThemePreferenceProvider } from "./theme/ThemePreferenceProvider";
import { SURFACE } from "./theme/tokens";
import { getAnalyticsIdentity, getAppVersion, getDeviceInfo, initAnalytics, setAnalyticsGlobalProperties, trackErrorEvent } from "./utils/analytics";
import { CAREERS_APPLICATION_ROUTE, CAREERS_PATH } from "./utils/careersRoutes";
import { REDIRECT_TO_SESSION_STORE_KEY } from "./utils/consts";
import { AppPlatform, ProfileMode } from "./utils/models";
import { baseURL } from "./utils/networkWrapper";
import { recurringShiftsPath } from "./utils/recurringShiftsRoutes";
import { staffingWeeklyPath } from "./utils/staffingRoutes";
import { isBedSideNurse, isHeadNurse } from "./utils/utilFunctions";
import { compareSemver, EnforcementMode, fetchAppVersionConfig, type PlatformVersionConfig } from "./utils/versionCheck";

const ENV = import.meta.env.VITE_ENVIROMENT || "development";

const LazyOpsBackoffice = lazy(() => import("./pages/OpsBackoffice/OpsBackoffice"));
const LazyOpsBackofficeDrillDown = lazy(() => import("./pages/OpsBackoffice/OpsBackofficeDrillDown"));
const LazySystemDataGridFixture = lazy(() => import("./pages/Dev/SystemDataGridFixture"));
const LazyConflictBlockedDialogFixture = lazy(() => import("./pages/Dev/ConflictBlockedDialogFixture"));
const LazyAirtableImportFixture = lazy(() => import("./pages/Dev/AirtableImportFixture"));
const LazyCalculateRecurringFixture = lazy(() => import("./pages/Dev/CalculateRecurringFixture"));
const LazyAgencyIntegrationToggleFixture = lazy(() => import("./pages/Dev/AgencyIntegrationToggleFixture"));
const LazyLocationsPage = lazy(() => import("./pages/Locations/LocationsPage"));
const LazyFillShiftReport = lazy(() => import("./pages/FillShiftReport/FillShiftReport"));
const LazyHiringHub = lazy(() => import("./pages/HiringHub/HiringHubPage"));
const LazyHiringHubProfile = lazy(() => import("./pages/HiringHub/profile/CandidateProfilePage"));
const LazyCareersPage = lazy(() => import("./pages/Careers/CareersPage"));
const LazyCareersCandidateProfile = lazy(() => import("./pages/Careers/profile/CandidateProfilePage"));
// The People roster pulls in PeopleGrid, ClickedCellEditor and StaffingChanges. Statically imported,
// all of that entered the initial bundle before `buildRoutes` had even evaluated the route's own
// rollout guard — a cost paid by every session for a view most of them cannot open. Named export, so
// the module is mapped to a default the way `lazy` requires.
const LazyStaffingPeopleViewRoute = lazy(() =>
	import("./components/staffing/PeopleView/StaffingPeopleViewRoute").then((module) => ({ default: module.StaffingPeopleViewRoute })),
);

import { useCareersAccess } from "./pages/Careers/hooks/useCareersAccess";
import { useHubTabAccessQuery } from "./pages/HiringHub/hooks/useHiringHub";

// ── Route access model ───────────────────────────────────────────────
type RouteCtx = { userInfo: UserInfo; isMobile: boolean; getDepartmentId: () => string | null };

/**
 * Mobile has no Monthly grid and no People roster, so both URLs fall back to the weekly grid.
 *
 * The path is built ABSOLUTELY on purpose. A relative `<Navigate to="staffing">` resolves against the
 * MATCHED route, so from `staffing/monthly/:date?` it produced `/:departmentId/staffing/monthly/staffing`
 * and the user landed on /404 rather than the grid. `useParams` is the honest source for the id here —
 * the route cannot match without it, so there is nothing to fall back to.
 */
const StaffingWeeklyRedirect: FC = () => {
	const { departmentId } = useParams();
	// Unreachable by construction — the route cannot match without the param — so home is the honest
	// destination rather than a thrown error: a crashed subtree is a heavier failure than a redirect that
	// lands one level wider than intended.
	return <Navigate to={departmentId === undefined ? "/" : staffingWeeklyPath(departmentId)} replace />;
};
/** "Schedule Template" was renamed to "Recurring Shifts" (APP-1008); bookmarks to the old URL still land on the page. */
const RecurringShiftsRedirect: FC = () => {
	const { departmentId } = useParams();
	const { search } = useLocation();
	// Unreachable by construction, as in `StaffingWeeklyRedirect` above.
	return <Navigate to={departmentId === undefined ? "/" : `${recurringShiftsPath(departmentId)}${search}`} replace />;
};
type AccessResult = true | { redirect: string };
type Access = (ctx: RouteCtx) => AccessResult;

// Presets — plain constants, each with its default deny target.
const PUBLIC: Access = () => true;
const ANY: Access = () => true;
// Reachable without login, but NOT open: the page validates a one-time link token
// (404s if missing) and the server rejects bad/expired tokens. Router passes through.
const TOKEN_GATED: Access = () => true;
const HEAD_NURSE: Access = ({ userInfo }) => (isHeadNurse(userInfo.role) ? true : { redirect: "/" });
const BEDSIDE: Access = ({ userInfo }) => (isBedSideNurse(userInfo.role) ? true : { redirect: "/" });
const SUPPORT: Access = ({ userInfo }) => (userInfo.support ? true : { redirect: "/404" });
const SUPPORT_OR_HEAD_NURSE: Access = ({ userInfo }) => (userInfo.support || isHeadNurse(userInfo.role) ? true : { redirect: "/" });

// ── Route node types + builder ───────────────────────────────────────
// A node is EITHER a guarded page, or a structural layout/prefix wrapper.
type PageNode = { path?: string; index?: boolean; access: Access; element: (ctx: RouteCtx) => ReactNode };
type LayoutNode = { path?: string; element?: ReactNode; children: RouteNode[] };
type RouteNode = PageNode | LayoutNode;

// Turn our nodes into React Router's route objects, applying the guard on each page.
function buildRoutes(nodes: RouteNode[], ctx: RouteCtx): RouteObject[] {
	return nodes.map((node) => {
		if ("access" in node) {
			const result = node.access(ctx); // allowed? or redirect?
			return {
				path: node.path,
				index: node.index,
				element: result === true ? node.element(ctx) : <Navigate to={result.redirect} replace />,
			} as RouteObject;
		}
		// layout/prefix: element defaults to <Outlet/> when omitted (a bare URL prefix)
		return { path: node.path, element: node.element, children: buildRoutes(node.children, ctx) } as RouteObject;
	});
}

const LoggedInRouteProviders: FC = () => {
	const { isLoggedIn, isCheckingAuth } = useAuth();
	const location = useLocation();
	const [shouldDelayLogin, setShouldDelayLogin] = useState(() => {
		// On mobile, always delay login redirect slightly on initial load
		// to give time for universal link detection
		return Capacitor.isNativePlatform();
	});

	useLayoutEffect(() => {
		// Check for pending universal link synchronously before paint
		if (!isLoggedIn && Capacitor.isNativePlatform()) {
			if (universalLinkService.hasPendingLaunchUrl()) {
				// Found a pending URL, delay longer to allow processing
				const timer = setTimeout(() => {
					setShouldDelayLogin(false);
				}, 2000);
				return () => clearTimeout(timer);
			}
			// No pending URL yet, but App.getLaunchUrl() might still be loading
			// Give it a brief moment
			const timer = setTimeout(() => {
				// Check again after a short delay
				if (universalLinkService.hasPendingLaunchUrl()) {
					// Found it, delay longer
					setTimeout(() => {
						setShouldDelayLogin(false);
					}, 1500);
				} else {
					setShouldDelayLogin(false);
				}
			}, 100);
			return () => clearTimeout(timer);
		}
		setShouldDelayLogin(false);
	}, [isLoggedIn]);

	if (!isLoggedIn) {
		// Don't redirect to login if we're still checking authentication or delaying for universal link
		if (isCheckingAuth || (shouldDelayLogin && Capacitor.isNativePlatform())) {
			return null; // Return nothing, preventing login page flash
		}
		sessionStorage.setItem(REDIRECT_TO_SESSION_STORE_KEY, location.pathname);
		return <Navigate to="/login" />;
	}

	return (
		<ChoseDepartmentProvider>
			<DepartmentLookSync />
			<FeatureFlagsProvider>
				<WeekIndexProvider>
					<TooltipProvider>
						<HospitalDataProvider>
							<SelfSchedulingProvider>
								<UserGuideChatProvider>
									<NotificationsProvider>
										<RequestsProvider>
											<ModalProvider>
												<RecommendationsDrawerProvider>
													<RevealWageProvider>
														<WeekViewProvider>
															<DraftModeProvider>
																<RequestUtilsProvider>
																	<UserSettingsProvider>
																		<GlobalManualLocationOrderProvider>
																			<RecommendationsProvider>
																				<PopupManagerProvider>
																					<HospitalDemoThemeProvider>
																						<CloseOpenShiftSuggestionProvider>
																							<Outlet />
																						</CloseOpenShiftSuggestionProvider>
																					</HospitalDemoThemeProvider>
																				</PopupManagerProvider>
																			</RecommendationsProvider>
																		</GlobalManualLocationOrderProvider>
																	</UserSettingsProvider>
																</RequestUtilsProvider>
															</DraftModeProvider>
														</WeekViewProvider>
													</RevealWageProvider>
												</RecommendationsDrawerProvider>
											</ModalProvider>
										</RequestsProvider>
									</NotificationsProvider>
								</UserGuideChatProvider>
							</SelfSchedulingProvider>
						</HospitalDataProvider>
					</TooltipProvider>
				</WeekIndexProvider>
			</FeatureFlagsProvider>
		</ChoseDepartmentProvider>
	);
};

const useGetDepartmentId = () => {
	//TODO: we should refactor the logic between ChosenDepartmentProvider and this hook to be more clear and not have duplicate logic around getting departmentId for redirects.
	// The main reason for this hook is to get departmentId in the RootRedirect component without having to use the ChosenDepartment context (which isn't set up yet at that level).
	// We can consider restructuring the app so that ChosenDepartmentProvider is available at a higher level, or we can centralize the logic for determining the "current" departmentId in a single place that both can use.

	// Helper hook to get departmentId for redirects
	const { userInfo } = useAuth();

	return () => {
		const storedDepartmentId = localStorageService.getChosenDepartmentID();
		if (storedDepartmentId && userInfo.departments?.some((dept) => dept.department_id === storedDepartmentId)) {
			return storedDepartmentId;
		}
		// Fall back to first department
		return userInfo.departments?.[0]?.department_id || null;
	};
};

const RootRedirect: FC = () => {
	const { userInfo } = useAuth();
	const isShiftInsightsEnabled = useFeatureFlag(FEATURE_FLAGS.ShiftInsightsTab);
	const careersAccess = useCareersAccess();
	const getDepartmentId = useGetDepartmentId();

	if (isBedSideNurse(userInfo.role)) return <Navigate to="/my-schedule" replace />;
	// Careers may be the landing page, and its flag is unknown until the department is chosen: hold here
	// rather than send a Careers user to Staffing on a cold load and never come back.
	if (careersAccess.loading) return <CircularProgress />;

	// Calculate redirect path for root route
	const getRootRedirectPath = (): string => {
		// With `hiring_ihh` on, Careers IS the landing page (bedside staff keep theirs, above). A deep link stored before login
		// still wins: the post-login effect in `App` navigates to it after this redirect has run.
		if (careersAccess.allowed) {
			return CAREERS_PATH;
		}

		const departmentId = getDepartmentId();
		if (isHeadNurse(userInfo.role) && isShiftInsightsEnabled) {
			return departmentId ? `/${departmentId}/shift-insights` : "/shift-insights";
		}

		return departmentId ? `/${departmentId}/staffing` : "/staffing:dateString?";
	};
	return <Navigate to={getRootRedirectPath()} replace />;
};

// Feature-flag gate lives INSIDE the element (rendered under FeatureFlagsProvider),
// not in `access` — the provider isn't available at the AppRoutes/buildRoutes level.
// Access = a hiring role (is_recruiter / is_interviewer / support), resolved by a backend call. While it's
// resolving we hold on a spinner rather than bouncing a legitimate role-user to "/". (The former `hiring_tab`
// feature flag was removed — access is role-based only.)
const useHiringTabAccess = (): { allowed: boolean; loading: boolean } => {
	const { data, isLoading } = useHubTabAccessQuery(true);
	if (isLoading) return { allowed: false, loading: true };
	return { allowed: data?.can_access ?? false, loading: false };
};

const HiringChatRoute: FC = () => {
	const { allowed, loading } = useHiringTabAccess();
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return (
		<Suspense fallback={<CircularProgress />}>
			<LazyHiringHub />
		</Suspense>
	);
};

const HiringHubProfileRoute: FC = () => {
	const { allowed, loading } = useHiringTabAccess();
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return (
		<Suspense fallback={<CircularProgress />}>
			<LazyHiringHubProfile />
		</Suspense>
	);
};

// Same shape as the Hiring routes: the gate lives INSIDE the element because the flag is read from
// `FeatureFlagsProvider`, which isn't mounted at the `buildRoutes` level.
const CareersRoute: FC<{ children: ReactNode }> = ({ children }) => {
	const { allowed, loading } = useCareersAccess();
	// A cold load of /careers renders once before the department (and so its flag) is known.
	if (loading) return <CircularProgress />;
	if (!allowed) return <Navigate to="/" replace />;
	return <Suspense fallback={<CircularProgress />}>{children}</Suspense>;
};

const routeRegistry: RouteNode[] = [
	{ path: "/login", access: PUBLIC, element: () => <SignIn /> },
	{ path: "/forgot-password", access: PUBLIC, element: () => <ForgotPassword /> },
	{ path: "/create-account", access: PUBLIC, element: () => <CreateAccount /> },
	{ path: "/reset-password", access: PUBLIC, element: () => <ResetPasswordOrWelcome /> },
	{ path: "/welcome", access: PUBLIC, element: () => <Welcome /> },
	{ path: "/otp", access: PUBLIC, element: () => <OneTimePassword /> },
	{ path: "/mfa", access: PUBLIC, element: () => <MultiFactorAuth /> },
	{ path: "/email-verification", access: PUBLIC, element: () => <EmailVerification /> },
	{ path: "/sftp-setup", access: TOKEN_GATED, element: () => <SftpSetupPage /> },
	{
		path: "/dev/system-datagrid",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<CircularProgress />}>
				<LazySystemDataGridFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/conflict-blocked-dialog",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<CircularProgress />}>
				<LazyConflictBlockedDialogFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/airtable-config-import",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<CircularProgress />}>
				<LazyAirtableImportFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/calculate-recurring",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<CircularProgress />}>
				<LazyCalculateRecurringFixture />
			</Suspense>
		),
	},
	{
		path: "/dev/agency-integration-toggle",
		access: () => (import.meta.env.DEV ? true : { redirect: "/404" }),
		element: () => (
			<Suspense fallback={<CircularProgress />}>
				<LazyAgencyIntegrationToggleFixture />
			</Suspense>
		),
	},
	{
		element: <LoggedInRouteProviders />,
		children: [
			{ path: "/", access: ANY, element: () => <RootRedirect /> },
			{
				element: <Layout />,
				children: [
					{ path: "/my-schedule", access: BEDSIDE, element: () => <BedsideHome /> },
					{ path: "/annie", access: BEDSIDE, element: () => <ChatBot /> },
					{ path: "/open-shifts", access: BEDSIDE, element: () => <OpenShifts /> },
					{ path: "/self-scheduling", access: BEDSIDE, element: () => <SelfSchedulingWrapper /> },
					{ path: "/notifications", access: ANY, element: () => <NotificationsPage /> },
					{
						path: "/day-schedule/:date",
						access: ANY,
						element: (c) => (c.isMobile || isBedSideNurse(c.userInfo.role) ? <DayScheduleMobile /> : <DaySchedule />),
					},
					{
						path: "/docs",
						children: [
							{ path: "admin-user-guides", access: HEAD_NURSE, element: () => <AdminUserGuides /> },
							{ path: "staff-user-guides", access: ANY, element: () => <StaffUserGuides /> },
						],
					},
					{ path: "/request/:id", access: ANY, element: (c) => <RequestRouteWrapper getDepartmentId={c.getDepartmentId} /> },
					{ path: "/hiring-chat", access: ANY, element: () => <HiringChatRoute /> },
					{ path: "/hiring-chat/candidate/:candidateId", access: ANY, element: () => <HiringHubProfileRoute /> },
					{
						path: CAREERS_PATH,
						access: ANY,
						element: () => (
							<CareersRoute>
								<LazyCareersPage />
							</CareersRoute>
						),
					},
					{
						path: CAREERS_APPLICATION_ROUTE,
						access: ANY,
						element: () => (
							<CareersRoute>
								<LazyCareersCandidateProfile />
							</CareersRoute>
						),
					},
					{
						path: "/ops-backoffice",
						access: SUPPORT,
						element: () => (
							<Suspense fallback={<CircularProgress />}>
								<LazyOpsBackoffice />
							</Suspense>
						),
					},
					{
						path: "/ops-backoffice/:departmentId",
						access: SUPPORT,
						element: () => (
							<Suspense fallback={<CircularProgress />}>
								<LazyOpsBackofficeDrillDown />
							</Suspense>
						),
					},
					{
						path: "/:departmentId",
						children: [
							{ path: "preferences", access: ANY, element: () => <Preferences /> },
							{ path: "shift-insights", access: ANY, element: () => <ChartContainer /> },
							// On a phone this forks between the gaps-first shell and the classic list — inside a
							// component, because the fork reads a feature flag and `FeatureFlagsProvider` renders
							// below this factory (see the note above `useHiringTabAccess`).
							{ path: "staffing/:dateString?", access: HEAD_NURSE, element: (c) => (c.isMobile ? <MobileStaffingRouteSwitch /> : <StaffingWrapper />) },
							// Classic keeps a URL of its own so "back to classic view" is linkable, survives a
							// reload, and survives a department switch (`resolveDepartmentSwitchDestination`). The
							// static segment outranks `:dateString?`, so "classic" is never read as a date.
							{ path: "staffing/classic/:dateString?", access: HEAD_NURSE, element: (c) => (c.isMobile ? <MobileStaffing /> : <StaffingWrapper />) },
							// Deliberately NOT forked: `staffing2` is a separate route that stays on classic.
							{ path: "staffing2/:dateString?", access: HEAD_NURSE, element: (c) => (c.isMobile ? <MobileStaffing /> : <StaffingWrapper />) },
							{
								path: "staffing/monthly/:date?",
								access: HEAD_NURSE,
								element: (c) => (c.isMobile ? <StaffingWeeklyRedirect /> : <MonthlyViewRoute />),
							},
							// The People roster is a view of the Staffing area like Day/Week/Month, so it is a URL
							// like them — linkable, bookmarkable, and Back-able. The static segment outranks
							// `staffing/:dateString?`, so "people" is never read as a date.
							{
								path: "staffing/people",
								access: HEAD_NURSE,
								element: (c) =>
									c.isMobile ? (
										<StaffingWeeklyRedirect />
									) : (
										<Suspense fallback={<CircularProgress />}>
											<LazyStaffingPeopleViewRoute />
										</Suspense>
									),
							},
							{
								path: "pbj-calculator",
								access: (c) => (isHeadNurse(c.userInfo.role) && !c.isMobile ? true : { redirect: "/" }),
								element: () => <PbjCalculator />,
							},
							{
								path: "pbj-model",
								access: (c) => (isHeadNurse(c.userInfo.role) && !c.isMobile ? true : { redirect: "/" }),
								element: () => <PbjModelPage />,
							},
							{ path: "employee-staffing/:id/:draftMode", access: HEAD_NURSE, element: () => <NurseSchedule /> },
							{ path: "command-center", access: HEAD_NURSE, element: () => <CommandCenter /> },
							{ path: "team", access: HEAD_NURSE, element: () => <Team /> },
							{ path: "add-nurse", access: HEAD_NURSE, element: () => <ProfileV2 initialProfileMode={ProfileMode.NEW} key="profile-create-only" /> },
							{ path: "nurse/:id", access: HEAD_NURSE, element: () => <ProfileV2 initialProfileMode={ProfileMode.VIEW} key="profile-view-or-edit" /> },
							{ path: "settings", access: ANY, element: () => <Settings /> },
							{ path: "settings/department-configuration", access: SUPPORT, element: () => <DepartmentConfiguration /> },
							{ path: "onboarding/tools", access: SUPPORT, element: () => <OnboardingToolsPage /> },
							{
								path: "settings/notification-settings",
								access: ANY,
								element: (c) => (isBedSideNurse(c.userInfo.role) ? <NotificationSettings /> : <AdminNotificationSettings />),
							},
							{ path: "planning", access: HEAD_NURSE, element: () => <PlanningOverview /> },
							{ path: "request/:id", access: ANY, element: (c) => <RequestRouteWrapper getDepartmentId={c.getDepartmentId} /> },
							{ path: "shift/:id", access: HEAD_NURSE, element: () => <ShiftData /> },
							{
								path: "post-shift/:dateString",
								access: (c) => (!isHeadNurse(c.userInfo.role) ? { redirect: "/" } : !c.isMobile ? { redirect: "/404" } : true),
								element: () => <PostShift />,
							},
							{ path: "recurring-shifts", access: HEAD_NURSE, element: () => <ScheduleTemplatePageWrapper /> },
							{ path: "master-schedule", access: HEAD_NURSE, element: () => <RecurringShiftsRedirect /> },
							{
								path: "locations",
								access: HEAD_NURSE,
								element: () => (
									<Suspense fallback={<CircularProgress />}>
										<LazyLocationsPage />
									</Suspense>
								),
							},
							{
								path: "day-schedule/:date",
								access: ANY,
								element: (c) => (c.isMobile || isBedSideNurse(c.userInfo.role) ? <DayScheduleMobile /> : <DaySchedule />),
							},
							{ path: "activity-log", access: SUPPORT_OR_HEAD_NURSE, element: () => <ActivityLogPage /> },
							{ path: "employee-changelog", access: HEAD_NURSE, element: () => <EmployeeChangelog /> },
							{
								path: "requests",
								access: (c) => (c.isMobile && isHeadNurse(c.userInfo.role) ? true : { redirect: "/" }),
								element: () => <RequestsRouteSwitch />,
							},
							{ path: "schedule", access: HEAD_NURSE, element: () => <Schedule /> },
							{ path: "hiring", access: HEAD_NURSE, element: () => <HiringModuleWrapper /> },
							{ path: "call-ins", access: HEAD_NURSE, element: () => <CallIns /> },
							{ path: "disciplinary-report", access: HEAD_NURSE, element: () => <DisciplinaryReport /> },
							{ path: "leakage-report", access: HEAD_NURSE, element: () => <LeakageReport /> },
							{
								path: "fill-shift-report",
								access: HEAD_NURSE,
								element: () => (
									<Suspense fallback={<CircularProgress />}>
										<LazyFillShiftReport />
									</Suspense>
								),
							},
							{ path: "shift-pickup-report", access: HEAD_NURSE, element: () => <ShiftPickupReport /> },
							{ path: "settings/notification-settings/:notificationType", access: ANY, element: () => <NotificationTypeSettings /> },
							{ path: "management-center", access: HEAD_NURSE, element: () => <ManagementCenter /> },
							{ path: "schedule-vs-actual", access: HEAD_NURSE, element: () => <ScheduleVsActualReport /> },
							{ path: "cost-deep-dive", access: HEAD_NURSE, element: () => <CostDeepDiveReport /> },
							{ path: "hours-summary", access: HEAD_NURSE, element: () => <HoursSummaryReport /> },
							{ path: "staff-satisfaction", access: HEAD_NURSE, element: () => <StaffSatisfactionReport /> },
							{ path: "management-center-multi", access: HEAD_NURSE, element: () => <ManagementCenterMulti /> },
							{ path: "patients/:dateString?", access: HEAD_NURSE, element: () => <Patients /> },
							{ path: "facilities/:facilityId?/:facilityName?", access: HEAD_NURSE, element: () => <FacilityPage /> },
							{
								path: "add-nurse-to-shift/:dateString",
								access: (c) => (isHeadNurse(c.userInfo.role) && c.isMobile ? true : { redirect: "/" }),
								element: () => <AddNurseToShift />,
							},
							{
								path: "send-message",
								access: (c) => (!isHeadNurse(c.userInfo.role) ? { redirect: "/" } : !c.isMobile ? { redirect: "/404" } : true),
								element: () => <BroadcastMessage isMobile={true} />,
							},
							{
								path: "messages",
								access: (c) => (!isHeadNurse(c.userInfo.role) ? { redirect: "/" } : !c.isMobile ? { redirect: "/404" } : true),
								element: () => <StaffCommunication />,
							},
						],
					},
				],
			},
		],
	},
	{ path: "/404", access: PUBLIC, element: () => <PageNotFound /> },
	{ path: "*", access: PUBLIC, element: () => <Navigate to="/404" replace /> },
];

const AppRoutes: FC = () => {
	const { userInfo } = useAuth();
	const { isMobile } = useIsMobile();
	const getDepartmentId = useGetDepartmentId();

	const ctx: RouteCtx = { userInfo, isMobile, getDepartmentId };
	return useRoutes(buildRoutes(routeRegistry, ctx));
};

function App() {
	const { isLoggedIn, userInfo, showTermsDialog } = useAuth();
	const { isMobile } = useIsMobile();
	const location = useLocation();
	const [deviceInfo, setDeviceInfo] = useState<Awaited<ReturnType<typeof getDeviceInfo>> | null>(null);
	const [appVersion, setAppVersion] = useState<string>("unknown");
	const [versionEnforcement, setVersionEnforcement] = useState<{
		mode: EnforcementMode.SOFT | EnforcementMode.HARD;
		platformConfig: PlatformVersionConfig;
	} | null>(null);
	const [showSoftUpdate, setShowSoftUpdate] = useState(false);
	const analyticsInitialized = useRef(false);
	useUniversalLink();

	// Fetch device info and app version when app loads (single source of truth for both useAnalytics and legacy trackEvent)
	useEffect(() => {
		const fetchInfo = async () => {
			if (Capacitor.isNativePlatform()) {
				const info = await getDeviceInfo();
				setDeviceInfo(info);
				const version = await getAppVersion();
				setAppVersion(version);
				// Update legacy globalProperties so deprecated trackEvent/trackErrorEvent also get device info + version
				setAnalyticsGlobalProperties({
					isApp: true,
					appVersion: version,
					deviceModel: info?.model ?? null,
					devicePlatform: info?.platform ?? null,
					deviceOsVersion: info?.osVersion ?? null,
					deviceManufacturer: info?.manufacturer ?? null,
				});
			}
		};
		fetchInfo();
	}, []);

	// Version enforcement check
	const checkAppVersion = useCallback(async (showSoft: boolean) => {
		if (!Capacitor.isNativePlatform()) return;
		const version = await getAppVersion();
		if (version === "unknown") return;
		const config = await fetchAppVersionConfig(baseURL);
		if (!config) return;
		const platform = Capacitor.getPlatform();
		const platformConfig = platform === AppPlatform.IOS ? config.ios : config.android;
		if (!platformConfig || platformConfig.enforcement_mode === EnforcementMode.OFF) {
			setVersionEnforcement(null);
			setShowSoftUpdate(false);
			return;
		}
		if (compareSemver(version, platformConfig.minimum_supported_version) < 0) {
			setVersionEnforcement({ mode: platformConfig.enforcement_mode, platformConfig });
			if (showSoft && platformConfig.enforcement_mode === EnforcementMode.SOFT) setShowSoftUpdate(true);
		} else {
			setVersionEnforcement(null);
			setShowSoftUpdate(false);
		}
	}, []);

	// On app launch: check version and show both soft and hard
	useEffect(() => {
		checkAppVersion(true);
	}, [checkAppVersion]);

	// On foreground resume: only re-check for hard mode (soft would be annoying)
	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return;
		const listener = CapacitorApp.addListener("appStateChange", ({ isActive }) => {
			if (isActive) checkAppVersion(false);
		});
		return () => {
			listener.then((l) => l.remove());
		};
	}, [checkAppVersion]);

	useEffect(() => {
		const initializeStatusBar = async () => {
			// Only initialize for Android
			if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === AppPlatform.Android) {
				try {
					// Set status bar style
					await StatusBar.setStyle({ style: Style.Light });

					// Set background color
					await StatusBar.setBackgroundColor({ color: SURFACE });

					// Make sure status bar doesn't overlay the webview
					await StatusBar.setOverlaysWebView({ overlay: false });

					console.log("Android status bar initialized successfully");
				} catch (error) {
					console.error("Failed to initialize Android status bar:", error);
				}
			}
		};

		initializeStatusBar();
	}, []);

	useEffect(() => {
		if (analyticsInitialized.current) return;
		initAnalytics();
		analyticsInitialized.current = true;
	}, []);

	const handleSessionInactive = useCallback(() => {
		try {
			mixpanel.stop_session_recording();
		} catch (error) {
			trackErrorEvent("Failed to stop session recording", error);
		}
	}, []);

	const handleSessionReturnFromInactive = useCallback(() => {
		try {
			mixpanel.start_session_recording();
		} catch (error) {
			trackErrorEvent("Failed to start new session recording", error);
		}
	}, []);

	// Support and impersonation sessions are internal, not real customer usage, so their session
	// replays waste Mixpanel's limited replay quota. Keeping the recording hook disabled for them
	// means start_session_recording() never fires (impersonation counts as support: the acting human
	// is support even though userInfo describes the impersonated customer). See APP-697.
	const isSupportOrImpersonationSession = !!userInfo?.support || !!userInfo?.impersonated_by;

	useOnReactClientInactive({
		timeoutMs: 15 * 60 * 1000 /*15 minutes*/,
		hookDisabled: !(
			analyticsInitialized.current &&
			ENV !== "development" &&
			isLoggedIn &&
			!!userInfo?.role &&
			isHeadNurse(userInfo.role) &&
			!isSupportOrImpersonationSession
		),
		onInactive: handleSessionInactive,
		onInitAndActiveAgain: handleSessionReturnFromInactive,
	});

	useEffect(() => {
		const analyticsIdentity = getAnalyticsIdentity(userInfo);

		if (analyticsIdentity && analyticsInitialized.current) {
			mixpanel.identify(analyticsIdentity);
			ReactGA.set({ userId: analyticsIdentity });
		}
		// The UI language is synced from the saved preference by I18nProvider, its only owner.
	}, [userInfo]);

	const navigate = useNavigate();
	useEffect(() => {
		const timerId = setTimeout(() => {
			const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_STORE_KEY);

			if (isLoggedIn && typeof redirectTo === "string") {
				sessionStorage.removeItem(REDIRECT_TO_SESSION_STORE_KEY);
				navigate(redirectTo, { replace: true });
			}
		}, 1_000);

		return () => {
			clearTimeout(timerId);
		};
	}, [isLoggedIn, navigate]);

	// Initialize push notifications when user ID changes
	useEffect(() => {
		if (userInfo?.id) {
			pushNotificationService.initialize(userInfo.id);
		}
	}, [userInfo?.id]);

	usePushSettingsTracker();

	const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

	// Roles are per-department; `AnalyticValues` is a flat scalar map, so the chosen department's role
	// list is joined into one filterable string ("" when the user has no role in it).
	const chosenDepartmentId = localStorageService.getChosenDepartmentID();
	const adminRolesForAnalytics = (userInfo.departments?.find((dept) => dept.department_id === chosenDepartmentId)?.adminRoles ?? []).join(",");

	// Hard enforcement: block the entire app
	if (versionEnforcement?.mode === EnforcementMode.HARD) {
		return (
			<ThemePreferenceProvider>
				<CssBaseline />
				<I18nProvider>
					<ForceUpdateScreen mode={EnforcementMode.HARD} />
				</I18nProvider>
			</ThemePreferenceProvider>
		);
	}

	return (
		<ThemePreferenceProvider>
			<CssBaseline />
			{showSoftUpdate && versionEnforcement && <ForceUpdateScreen mode={EnforcementMode.SOFT} onDismiss={() => setShowSoftUpdate(false)} />}
			<I18nProvider>
				<AnalyticValuesProvider
					props={{
						userId: userInfo.id,
						username: getAnalyticsIdentity(userInfo),
						isLoggedIn,
						isMobile,
						pathname: location.pathname,
						department_id: localStorageService.getChosenDepartmentID() || userInfo.departments?.map((d) => d.department_id).join(","),
						admin_roles: adminRolesForAnalytics,
						// Impersonation counts as support activity: the acting human is support even though
						// userInfo describes the impersonated customer. Both cases must be filterable in Mixpanel.
						is_support_user: !!userInfo.support || !!userInfo.impersonated_by,
						// The impersonator's identifier, tagged onto every event so impersonation sessions
						// are attributable to the acting human. Null (not undefined) for real customer
						// sessions so the property is present and queryable rather than silently absent.
						impersonated_by: userInfo.impersonated_by ?? null,
						isApp: Capacitor.isNativePlatform(),
						appVersion: appVersion ?? "unknown",
						deviceModel: deviceInfo?.model ?? null,
						devicePlatform: deviceInfo?.platform ?? null,
						deviceOsVersion: deviceInfo?.osVersion ?? null,
						deviceManufacturer: deviceInfo?.manufacturer ?? null,
					}}
				>
					<GoogleOAuthProvider clientId={googleClientId}>
						<AppRoutes />
						{showTermsDialog && <TermsAndConditionsDialog open={showTermsDialog} />}
					</GoogleOAuthProvider>
				</AnalyticValuesProvider>
			</I18nProvider>
		</ThemePreferenceProvider>
	);
}

export default App;
